import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AvailabilityAdmin } from "@/components/admin/AvailabilityAdmin";
import { PricesAdmin } from "@/components/admin/PricesAdmin";
import { InquiriesAdmin } from "@/components/admin/InquiriesAdmin";
import { ReviewsAdmin } from "@/components/admin/ReviewsAdmin";
import { ActivityAdmin } from "@/components/admin/ActivityAdmin";
import { StatsAdmin } from "@/components/admin/StatsAdmin";
import { SecurityAdmin } from "@/components/admin/SecurityAdmin";
import { MaintenanceAdmin } from "@/components/admin/MaintenanceAdmin";
import { Eye, LogOut } from "lucide-react";

export default function AdminDashboard() {
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => navigate("/admin/login"))
            .finally(() => setChecking(false));
    }, [navigate]);

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        toast.success("Abgemeldet.");
        navigate("/admin/login");
    };

    if (checking) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#120E09] text-sm text-[#9C917D]" data-testid="admin-loading">
                Lade …
            </div>
        );
    }
    if (!user) return null;

    return (
        <div className="min-h-screen bg-[#120E09]" data-testid="admin-dashboard">
            <header className="border-b border-[#2E2517] bg-[#1B150E]">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-4">
                    <Link to="/" aria-label="Zur Website"><Logo size={34} /></Link>
                    <div className="flex items-center gap-3">
                        <a
                            data-testid="admin-preview-link"
                            href="/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 rounded-full border border-[#2E2517] px-4 py-2 text-xs text-[#9C917D] transition-colors duration-200 hover:border-[#C88A45]/40 hover:text-[#EDE6DA]"
                        >
                            <Eye size={14} /> Vorschau
                        </a>
                        <span className="hidden text-sm text-[#9C917D] sm:block">{user.email}</span>
                        <button
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="flex items-center gap-2 rounded-full border border-[#2E2517] px-4 py-2 text-xs text-[#9C917D] transition-colors duration-200 hover:border-[#C88A45]/40 hover:text-[#EDE6DA]"
                        >
                            <LogOut size={14} /> Abmelden
                        </button>
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-7xl px-6 py-10">
                <h1 className="mb-8 font-display text-3xl font-extrabold uppercase tracking-tight text-[#EDE6DA]">Verwaltung</h1>
                <Tabs defaultValue="verfuegbarkeit">
                    <TabsList className="mb-8 flex h-auto flex-wrap border border-[#2E2517] bg-[#1B150E]">
                        <TabsTrigger data-testid="admin-tab-verfuegbarkeit" value="verfuegbarkeit">Verfügbarkeit</TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-preise" value="preise">Preisliste</TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-anfragen" value="anfragen">Anfragen</TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-bewertungen" value="bewertungen">Bewertungen</TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-aktivitaet" value="aktivitaet">Aktivität</TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-statistik" value="statistik">Statistik</TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-sicherheit" value="sicherheit">Sicherheit</TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-wartung" value="wartung">Wartung</TabsTrigger>
                    </TabsList>
                    <TabsContent value="verfuegbarkeit"><AvailabilityAdmin /></TabsContent>
                    <TabsContent value="preise"><PricesAdmin /></TabsContent>
                    <TabsContent value="anfragen"><InquiriesAdmin /></TabsContent>
                    <TabsContent value="bewertungen"><ReviewsAdmin /></TabsContent>
                    <TabsContent value="aktivitaet"><ActivityAdmin /></TabsContent>
                    <TabsContent value="statistik"><StatsAdmin /></TabsContent>
                    <TabsContent value="sicherheit"><SecurityAdmin /></TabsContent>
                    <TabsContent value="wartung"><MaintenanceAdmin /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
